/**
 * Pure helpers for the auto-join rule condition builder. Converts between the
 * backend `AutoJoinCondition` shape and the flat editable UI "row" the modal
 * renders. Kept out of the component so they can be unit-tested directly.
 */
import type {
  AutoJoinCondition,
  AutoJoinOperator,
  ReconcileUserResult,
} from '../types/spaces';

/** UI row: holds the value as a single editable string regardless of operator. */
export interface ConditionRow {
  id: string;
  attribute: string;
  operator: AutoJoinOperator;
  /** Comma-separated for `in`; a userId for a manager; plain text otherwise. */
  value: string;
}

let rowSeq = 0;
export function newConditionRow(): ConditionRow {
  rowSeq += 1;
  return { id: `r${rowSeq}`, attribute: 'department', operator: 'eq', value: '' };
}

export function conditionToRow(c: AutoJoinCondition): ConditionRow {
  rowSeq += 1;
  const value = Array.isArray(c.value) ? c.value.join(', ') : (c.value ?? '');
  return { id: `r${rowSeq}`, attribute: c.attribute, operator: c.operator, value };
}

export function rowToCondition(r: ConditionRow): AutoJoinCondition {
  if (r.operator === 'exists') {
    return { attribute: r.attribute, operator: 'exists' };
  }
  if (r.operator === 'in') {
    const value = r.value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    return { attribute: r.attribute, operator: 'in', value };
  }
  return { attribute: r.attribute, operator: r.operator, value: r.value.trim() };
}

export function rowValid(r: ConditionRow): boolean {
  if (r.operator === 'exists') return true;
  if (r.operator === 'in') {
    return r.value.split(',').map((v) => v.trim()).filter(Boolean).length > 0;
  }
  return r.value.trim().length > 0;
}

const OPERATOR_LABEL: Record<string, string> = {
  eq: 'equals',
  in: 'is one of',
  startsWith: 'starts with',
  exists: 'is set',
};

/** Human-readable one-liner for a condition, used in the rules list. */
export function summarizeCondition(c: AutoJoinCondition): string {
  if (c.operator === 'exists') return `${c.attribute} ${OPERATOR_LABEL.exists}`;
  const v = Array.isArray(c.value) ? c.value.join(', ') : c.value;
  return `${c.attribute} ${OPERATOR_LABEL[c.operator] ?? c.operator} "${v}"`;
}

/**
 * Turn a targeted-reconcile result into a user-facing toast. Three outcomes:
 * - joined → success
 * - didn't join but has attributes → no rule matched (info)
 * - didn't join and no attributes → likely stale directory data (info)
 */
export function describeReconcileOutcome(
  result: ReconcileUserResult,
  name: string,
): { variant: 'success' | 'info'; message: string } {
  if (result.joinedThisSpace) {
    return { variant: 'success', message: `Added ${name} via auto-join rules` };
  }
  if (result.hasAttributes) {
    return {
      variant: 'info',
      message: `${name} doesn't match any auto-join rule for this space`,
    };
  }
  return {
    variant: 'info',
    message: `${name} has no synced directory attributes yet — ask them to sign in again`,
  };
}
